import React, { useState, useEffect, useCallback, memo } from 'react';
import { useAlert } from '../../Context/AlertContext';

// Individual Alert Component
const AlertItem = memo(({
  id,
  status,
  message,
  autoHideDuration = 5000,
  position = 'top-right'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const { removeAlert } = useAlert();

  // Handle auto hide
  useEffect(() => {
    if (autoHideDuration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [autoHideDuration]);

  // Optimized close handler
  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      removeAlert(id);
    }, 200); // Animation duration
  }, [id, removeAlert]);

  if (!isVisible) return null;

  // Alert styles based on status
  const getAlertStyles = (status) => {
    const baseStyles = {
      padding: '16px 20px',
      borderRadius: '8px',
      marginBottom: '12px',
      minWidth: '320px',
      maxWidth: '450px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: '1px solid',
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      transition: 'all 0.2s ease-out',
      transform: isExiting ? 'translateX(100%)' : 'translateX(0)',
      opacity: isExiting ? 0 : 1,
      animation: !isExiting ? 'slideInRight 0.3s ease-out' : 'none'
    };

    const statusStyles = {
      success: {
        backgroundColor: '#d4edda',
        borderColor: '#c3e6cb',
        color: '#155724'
      },
      error: {
        backgroundColor: '#f8d7da',
        borderColor: '#f5c6cb',
        color: '#721c24'
      },
      warning: {
        backgroundColor: '#fff3cd',
        borderColor: '#ffeaa7',
        color: '#856404'
      },
      info: {
        backgroundColor: '#d1ecf1',
        borderColor: '#bee5eb',
        color: '#0c5460'
      }
    };

    return { ...baseStyles, ...statusStyles[status] };
  };

  // Get icon based on status
  const getIcon = (status) => {
    const iconStyle = {
      fontSize: '20px',
      fontWeight: 'bold',
      marginTop: '2px'
    };

    const icons = {
      success: { icon: '✓', color: '#28a745' },
      error: { icon: '✕', color: '#dc3545' },
      warning: { icon: '⚠', color: '#ffc107' },
      info: { icon: 'ℹ', color: '#17a2b8' }
    };

    const { icon, color } = icons[status] || { icon: '•', color: '#6c757d' };

    return (
      <span style={{ ...iconStyle, color }}>
        {icon}
      </span>
    );
  };

  return (
    <>
      <div style={getAlertStyles(status)}>
        {getIcon(status)}
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '4px',
            textTransform: 'capitalize'
          }}>
            {status}
          </div>
          <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
            {message}
          </div>
        </div>
        <button
          onClick={handleClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            color: 'inherit',
            opacity: 0.7,
            transition: 'opacity 0.2s',
            marginTop: '2px'
          }}
          onMouseOver={(e) => e.target.style.opacity = 1}
          onMouseOut={(e) => e.target.style.opacity = 0.7}
        >
          ×
        </button>
      </div>
      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
});

// Alert Container Component
const AlertContainer = memo(({ position = 'top-right' }) => {
  const { alerts } = useAlert();

  // Position styles
  const getPositionStyles = (position) => {
    const baseStyles = {
      position: 'fixed',
      zIndex: 9999,
      pointerEvents: 'none'
    };

    const positions = {
      'top-right': { top: '20px', right: '20px' },
      'top-left': { top: '20px', left: '20px' },
      'bottom-right': { bottom: '20px', right: '20px' },
      'bottom-left': { bottom: '20px', left: '20px' },
      'top-center': { top: '20px', left: '50%', transform: 'translateX(-50%)' },
      'bottom-center': { bottom: '20px', left: '50%', transform: 'translateX(-50%)' }
    };

    return { ...baseStyles, ...positions[position] };
  };

  if (alerts.length === 0) return null;

  return (
    <div style={getPositionStyles(position)}>
      {alerts.map(alert => (
        <div key={alert.id} style={{ pointerEvents: 'auto' }}>
          <AlertItem {...alert} position={position} />
        </div>
      ))}
    </div>
  );
});

export default AlertContainer;